import { ActionIcon, Box, Button, Drawer, Group } from '@mantine/core';
import { IconMessagePlus, IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { AdminSettingsButton } from '~portal/components/AdminSettingsButton';
import { ColorSchemeToggle } from '~portal/components/ColorSchemeToggle';
import { clip } from '~portal/components/common/common.css';
import { Cmp, logger, trackAction } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';
import { drawerSheet } from './ChatHistoryDrawer.css';
import { ChatSidebarContent } from './ChatSidebar';

export type ChatHistoryDrawerProps = {
  opened: boolean;
  onClose: () => void;
};

/**
 * @desc Mobile chat history drawer.
 * Slides in from left edge on small displays (<md breakpoint).
 */
export const ChatHistoryDrawer: Cmp<ChatHistoryDrawerProps> = function ({ opened, onClose }) {
  const navigate = useNavigate();

  const handleNewChat = () => {
    trackAction('new_chat_started', { surface: 'drawer' });
    navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } })
      .then(() => onClose())
      .catch((err) => {
        logger.error('[ChatHistoryDrawer] Navigation to new chat failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      // Mantine's `sm` is a flat 380px, which is wider than a 360px phone — the drawer then
      // hangs off the right edge and its close button goes with it. `min()` keeps the desktop
      // width and clamps it to the viewport on anything narrower.
      size="min(380px, 100vw)"
      position="left"
      // The sheet's own inset, one step down from Mantine's `md` default: below `md` the drawer
      // IS the sidebar, and every millimetre of gutter is a millimetre of thread list. `sm` is
      // still a comfortable thumb's margin from the screen edge.
      padding="sm"
      withOverlay
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.5 }}
      classNames={{ content: drawerSheet }}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/*
        The drawer's header: start a thread, the settings that used to sit in the chat
        sub-header, and the way out.

        The admin cog and the scheme control moved here from the sub-header (H2 mobile review).
        On a 375px header they were two controls a user touches once a session, taking room from
        the one thing that says which conversation is open; in the drawer they sit with the other
        session-level chrome — the thread list and the account block — one tap from anywhere.
        Both builds: the platform's global nav, which carries them elsewhere, stays out of the
        chat layout.
      */}
      <Group justify="space-between" wrap="nowrap" gap="xs" pb="sm">
        <Button
          leftSection={<IconMessagePlus size={18} />}
          onClick={handleNewChat}
          variant="filled"
          size="sm"
          data-dd-action-name="New chat"
        >
          New Chat
        </Button>
        <Group gap="xs" wrap="nowrap">
          <AdminSettingsButton />
          <ColorSchemeToggle />
          <ActionIcon
            onClick={onClose}
            variant="subtle"
            size="lg"
            className={touchTarget}
            aria-label="Close chat history"
          >
            <IconX size={20} />
          </ActionIcon>
        </Group>
      </Group>

      {/* The list takes the sheet's own padding — see `padding="sm"` — so the account block at
          its foot keeps that one inset and nothing doubles it. */}
      <Box flex={1} className={clip}>
        {/* Below md the sidebar IS this drawer, so the account block lives at its bottom —
            the same place the desktop rail puts it. */}
        <ChatSidebarContent onItemClick={onClose} hideNewChatButton showAccount />
      </Box>
    </Drawer>
  );
};
